import { FormattedNumber, useIntl } from 'react-intl'

import type { ColumnDef } from '@gto/ui/data-table'
import { createColumns } from '@gto/ui/data-table'
import type { TypographyColor } from '@gto/ui/typography'
import { Typography } from '@gto/ui/typography'

import type { DataTableProps } from '@/components/data-table'
import { DataTable } from '@/components/data-table'
import { StatusBadge } from '@/components/status-badge'
import { fluidColumn } from '@/components/table-columns'
import { toAmount } from '@/utils/to-amount'

const PAYOUT_AMOUNT_STYLE: Record<string, { color: TypographyColor; textDecoration?: string }> = {
  PAID: { color: 'primary' },
  REQUESTED: { color: 'text.primary' },
  REJECTED: { color: 'text.disabled', textDecoration: 'line-through' },
  CANCELLED: { color: 'text.disabled', textDecoration: 'line-through' },
  REVERSED: { color: 'text.disabled', textDecoration: 'line-through' },
}

// A payout in one of these states is void — the row dims and its amount is struck
// through, so a resolved-away request never reads as money still owed.
const VOID_STATUSES = new Set(['REJECTED', 'CANCELLED', 'REVERSED'])

// Structural floor every `PayoutsTable` row must satisfy. Both
// `AffiliateAdminPayoutSchema` and `AffiliatePayoutSchema` (partner-facing)
// match this shape today — admin-only fields (`partner`, `resolved_by_email`)
// are optional and only read by the columns gated on `showPartner` /
// `showResolvedBy`. Pinning the table to this row type (no `<R>` generic)
// removes per-call-site cast pressure while still flowing both shapes through.
export type PayoutRow = {
  id: string
  status: string
  requested_amount: number | string
  payout_method: string | null
  created_at: string
  resolved_at?: string | null
  partner?: { display_name: string; email: string }
  resolved_by_email?: string | null
}

type PayoutColumnsOptions = {
  showPartner?: boolean
  showResolvedBy?: boolean
}

const usePayoutColumns = (options?: PayoutColumnsOptions): ColumnDef<PayoutRow>[] => {
  const { formatMessage } = useIntl()
  const { showPartner = false, showResolvedBy = false } = options ?? {}

  return createColumns<PayoutRow>([
    ...(showPartner
      ? [
          {
            id: 'partner',
            // An accessor (not a plain display column) so the sort key stays the
            // display name, matching the old `valueGetter`.
            accessorFn: (row) => row.partner?.display_name ?? '',
            header: formatMessage({ id: 'admin.payouts.partner' }),
            ...fluidColumn({ min: 150, flex: 1.3, stacked: true }),
            enableSorting: false,
            cell: ({ row }) => (
              <div className="flex flex-col">
                {row.original.partner?.display_name ?? '—'}
                <Typography
                  color="text.secondary"
                  variant="caption"
                >
                  {row.original.partner?.email ?? ''}
                </Typography>
              </div>
            ),
          } satisfies ColumnDef<PayoutRow>,
        ]
      : []),
    {
      accessorKey: 'created_at',
      header: formatMessage({ id: 'payouts.date' }),
      ...fluidColumn({ min: 150, flex: 1.2 }),
      cell: ({ row }) =>
        new Date(row.original.created_at).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        }),
    },
    {
      accessorKey: 'requested_amount',
      header: formatMessage({ id: 'payouts.amount' }),
      ...fluidColumn({ min: 100, flex: 0.8, align: 'right' }),
      cell: ({ row }) => {
        const style = PAYOUT_AMOUNT_STYLE[row.original.status] ?? { color: 'text.primary' }

        return (
          <Typography
            color={style.color}
            style={{ textDecoration: style.textDecoration }}
            variant="body2"
          >
            <FormattedNumber
              currency="USD"
              style="currency"
              value={toAmount(row.original.requested_amount, 'payouts table requested_amount:')}
            />
          </Typography>
        )
      },
    },
    {
      accessorKey: 'payout_method',
      header: formatMessage({ id: 'payouts.method' }),
      ...fluidColumn({ min: 80, flex: 0.6 }),
      cell: ({ row }) => (
        <Typography
          color="text.secondary"
          variant="body2"
        >
          {row.original.payout_method ?? '—'}
        </Typography>
      ),
    },
    ...(showResolvedBy
      ? [
          {
            accessorKey: 'resolved_by_email',
            header: formatMessage({ id: 'admin.payouts.resolvedBy' }),
            ...fluidColumn({ min: 120, flex: 1 }),
            cell: ({ row }) => (
              <Typography
                color="text.secondary"
                variant="body2"
              >
                {row.original.resolved_by_email != null && row.original.resolved_by_email !== ''
                  ? row.original.resolved_by_email
                  : '—'}
              </Typography>
            ),
          } satisfies ColumnDef<PayoutRow>,
        ]
      : []),
    {
      accessorKey: 'resolved_at',
      header: formatMessage({ id: 'payouts.resolvedDate' }),
      ...fluidColumn({ min: 100, flex: 0.8 }),
      cell: ({ row }) => {
        if (row.original.resolved_at == null) {
          return (
            <Typography
              color="text.disabled"
              variant="body2"
            >
              —
            </Typography>
          )
        }

        return (
          <Typography
            color="text.secondary"
            variant="body2"
          >
            {new Date(row.original.resolved_at).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })}
          </Typography>
        )
      },
    },
    {
      accessorKey: 'status',
      header: formatMessage({ id: 'payouts.status' }),
      ...fluidColumn({ min: 80, flex: 0.6, align: 'right' }),
      cell: ({ row }) => <StatusBadge status={row.original.status} />,
    },
  ])
}

type PayoutsTableProps = {
  showPartner?: boolean
  showResolvedBy?: boolean
} & Omit<DataTableProps<PayoutRow>, 'columns' | 'getRowClassName'>

export const PayoutsTable = ({ showPartner, showResolvedBy, ...rest }: PayoutsTableProps) => {
  const columns = usePayoutColumns({ showPartner, showResolvedBy })

  return (
    <DataTable<PayoutRow>
      columns={columns}
      getRowClassName={(row) => (VOID_STATUSES.has(row.status) ? 'opacity-45' : undefined)}
      {...rest}
    />
  )
}
